// src/pages/core/CorFoodIngredientsList.tsx

import React, { useState, useMemo } from 'react';
import { Input, InputNumber, Space, Divider, Typography, Spin, Alert, Row, Col, Select, Drawer, Form, AutoComplete, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusOutlined, EditOutlined, SearchOutlined, TagOutlined, SaveOutlined } from '@ant-design/icons';

import { CorFoodIngredients } from '../../types';
import { fetchSqlData, insertData, updateData } from '../../services/api';
import { useTablePagination } from '../../components/common/useTablePagination';
import StandardTable from '../../components/common/StandardTable';
import Button from '../../components/common/Button';

const { Title, Text } = Typography;

type IngredientFormData = {
  name: string;
  category: string;
  calories?: number | null;
  protein_gr?: number | null;
  fat_gr?: number | null;
  carbo_gr?: number | null;
  standard_ref?: string | null;
  database_source?: string | null;
  referred_food?: string | null;
};

const emptyIngredient: IngredientFormData = {
  name: '',
  category: '',
  calories: null,
  protein_gr: null,
  fat_gr: null,
  carbo_gr: null,
  standard_ref: '',
  database_source: '',
  referred_food: '',
};

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatNutrient(value: unknown): string {
  const n = asNumber(value);
  if (n === null) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

function varianceColor(value: unknown): string | undefined {
  const n = asNumber(value);
  if (n === null) return undefined;
  const abs = Math.abs(n);
  if (abs <= 1) return '#389e0d';
  if (abs <= 5) return '#d48806';
  return '#cf1322';
}

function compareNumeric(a: unknown, b: unknown): number {
  const na = asNumber(a);
  const nb = asNumber(b);
  if (na === null && nb === null) return 0;
  if (na === null) return 1;
  if (nb === null) return -1;
  return na - nb;
}

function formValuesFromRecord(record: CorFoodIngredients): IngredientFormData {
  return {
    name: record.name,
    category: record.category || '',
    calories: asNumber(record.calories),
    protein_gr: asNumber(record.protein_gr),
    fat_gr: asNumber(record.fat_gr),
    carbo_gr: asNumber(record.carbo_gr),
    standard_ref: record.standard_ref || '',
    database_source: record.database_source || '',
    referred_food: record.referred_food || '',
  };
}

function ingredientPayload(values: IngredientFormData) {
  return {
    name: values.name.trim(),
    category: values.category.trim(),
    calories: asNumber(values.calories),
    protein_gr: asNumber(values.protein_gr),
    fat_gr: asNumber(values.fat_gr),
    carbo_gr: asNumber(values.carbo_gr),
    standard_ref: values.standard_ref?.trim() || null,
    database_source: values.database_source?.trim() || null,
    referred_food: values.referred_food?.trim() || null,
  };
}

function ingredientErrorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string; message?: string; details?: string } } })?.response?.data;
  const detail = data?.details || data?.error || data?.message || '';
  if (detail.includes('duplicate key value violates unique constraint') && detail.includes('(name)=')) {
    const match = detail.match(/\(name\)=\((.*?)\)/);
    const duplicateName = match ? match[1] : 'this name';
    return `An ingredient with the name "${duplicateName}" already exists. Please choose a different name.`;
  }
  if (data?.error || data?.message) return `Server error: ${data.error || data.message}`;
  return err instanceof Error ? `${fallback}: ${err.message}` : fallback;
}

function uniqueOptions(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => !!value))).sort();
}

const CorFoodIngredientsList: React.FC = () => {
  const [nameSearchText, setNameSearchText] = useState('');
  const [categorySearchText, setCategorySearchText] = useState('');
  const [sourceSearchText, setSourceSearchText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CorFoodIngredients | null>(null);
  const [form] = Form.useForm<IngredientFormData>();
  const queryClient = useQueryClient();

  const { getPaginationProps } = useTablePagination(15);

  const {
    data: ingredientsData,
    isLoading,
    error,
  } = useQuery<CorFoodIngredients[], Error>({
    queryKey: ['corFoodIngredients'],
    queryFn: () => fetchSqlData('cor_food_ingredients', undefined, undefined),
    staleTime: 1000 * 60 * 5,
  });

  const categoryOptions = useMemo(() => {
    const uniqueCategories = uniqueOptions((ingredientsData ?? []).map((item) => item.category));
    return [{ label: 'All Categories', value: '' }, ...uniqueCategories.map((cat) => ({ label: cat, value: cat }))];
  }, [ingredientsData]);

  const sourceOptions = useMemo(() => {
    const uniqueSources = uniqueOptions((ingredientsData ?? []).map((item) => item.database_source));
    return [{ label: 'All Sources', value: '' }, ...uniqueSources.map((source) => ({ label: source, value: source }))];
  }, [ingredientsData]);

  const filteredData = useMemo(() => {
    if (!ingredientsData) return [];
    const lowerNameSearch = nameSearchText.toLowerCase();
    if (!lowerNameSearch && !categorySearchText && !sourceSearchText) {
      return ingredientsData;
    }
    return ingredientsData.filter((item) => {
      const nameMatch = !lowerNameSearch || item.name.toLowerCase().includes(lowerNameSearch);
      const categoryMatch = !categorySearchText || item.category === categorySearchText;
      const sourceMatch = !sourceSearchText || item.database_source === sourceSearchText;
      return nameMatch && categoryMatch && sourceMatch;
    });
  }, [ingredientsData, nameSearchText, categorySearchText, sourceSearchText]);

  const categorySuggestions = useMemo(
    () => uniqueOptions((ingredientsData ?? []).map((item) => item.category)).map((category) => ({ value: category })),
    [ingredientsData],
  );

  const sourceSuggestions = useMemo(
    () => uniqueOptions((ingredientsData ?? []).map((item) => item.database_source)).map((source) => ({ value: source })),
    [ingredientsData],
  );

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue(emptyIngredient);
    setDrawerOpen(true);
  };

  const openEdit = (record: CorFoodIngredients) => {
    setEditing(record);
    form.setFieldsValue(formValuesFromRecord(record));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const { mutate: saveIngredient, isPending: isSaving } = useMutation<unknown, Error, IngredientFormData>({
    mutationFn: (values) => {
      const payload = ingredientPayload(values);
      if (editing) return updateData('cor_food_ingredients', editing.id, payload);
      return insertData('cor_food_ingredients', payload);
    },
    onSuccess: (responseData, values) => {
      if (!editing) {
        const ids = (responseData as { ids?: number[] } | undefined)?.ids;
        if (!Array.isArray(ids) || ids.length === 0) {
          message.error('Failed to get valid ID after creation.');
          return;
        }
        message.success(`Ingredient "${values.name}" (ID: ${ids[0]}) created successfully!`);
      } else {
        message.success(`Ingredient "${values.name}" updated successfully!`);
      }
      queryClient.invalidateQueries({ queryKey: ['corFoodIngredients'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(ingredientErrorText(err, editing ? 'Failed to update ingredient' : 'Failed to create ingredient'));
    },
  });

  const numericColumn = (
    title: React.ReactNode,
    dataIndex: keyof CorFoodIngredients,
    width = 64,
  ): ColumnsType<CorFoodIngredients>[number] => ({
    title,
    dataIndex,
    key: String(dataIndex),
    align: 'right',
    width,
    sorter: (a, b) => compareNumeric(a[dataIndex], b[dataIndex]),
    render: (value: unknown) => formatNutrient(value),
  });

  const columns: ColumnsType<CorFoodIngredients> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 70,
    },
    {
      title: 'Ingredient Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: true,
      width: 165,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
      filters: categoryOptions
        .filter((opt) => opt.value !== '')
        .map((opt) => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.category === value,
      width: 140,
    },
    numericColumn('kcal', 'calories', 64),
    numericColumn('Prot', 'protein_gr', 58),
    numericColumn('Fat', 'fat_gr', 52),
    numericColumn('Carb', 'carbo_gr', 58),
    numericColumn('Atw', 'atwatercheck', 52),
    {
      title: 'Var',
      dataIndex: 'variance',
      key: 'variance',
      align: 'right',
      width: 52,
      sorter: (a, b) => compareNumeric(a.variance, b.variance),
      render: (value: unknown) => (
        <Text style={{ color: varianceColor(value) }}>{formatNutrient(value)}</Text>
      ),
    },
    {
      title: 'Standard Ref',
      dataIndex: 'standard_ref',
      key: 'standard_ref',
      ellipsis: true,
      width: 140,
      render: (value: string | null | undefined) => value || '—',
    },
    {
      title: 'Source',
      dataIndex: 'database_source',
      key: 'database_source',
      width: 140,
      ellipsis: true,
      filters: sourceOptions
        .filter((opt) => opt.value !== '')
        .map((opt) => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.database_source === value,
      render: (value: string | null | undefined) => value || '—',
    },
    {
      title: 'Referred Food',
      dataIndex: 'referred_food',
      key: 'referred_food',
      ellipsis: true,
      width: 140,
      sorter: (a, b) => (a.referred_food || '').localeCompare(b.referred_food || ''),
      render: (value: string | null | undefined) => value || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 72,
      render: (_, record) => (
        <Button action="edit" size="small" type="primary" icon={<EditOutlined />} onClick={() => openEdit(record)} title="Edit Ingredient" />
      ),
    },
  ];

  if (error) {
    return <Alert message="Error" description={`Failed to load ingredients: ${error.message}`} type="error" showIcon />;
  }

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <TagOutlined style={{ marginRight: 8 }} /> Food Ingredients
            </Title>
          </Col>
          <Col>
            <Button action="add" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create New Ingredient
            </Button>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search by Name"
              prefix={<SearchOutlined />}
              value={nameSearchText}
              onChange={(e) => setNameSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              showSearch
              placeholder="Select Category"
              value={categorySearchText}
              onChange={(value) => setCategorySearchText(value ?? '')}
              style={{ width: '100%' }}
              options={categoryOptions}
              allowClear
              loading={isLoading}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              showSearch
              placeholder="Select Source"
              value={sourceSearchText}
              onChange={(value) => setSourceSearchText(value ?? '')}
              style={{ width: '100%' }}
              options={sourceOptions}
              allowClear
              loading={isLoading}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredData}
            rowKey="id"
            loading={isLoading}
            pagination={getPaginationProps(filteredData.length)}
            bordered
            size="small"
          />
        </Spin>

        <Drawer
          title={editing ? `Edit Food Ingredient: ${editing.name}` : 'Create New Food Ingredient'}
          open={drawerOpen}
          onClose={closeDrawer}
          width={560}
          destroyOnClose
          afterOpenChange={(visible) => {
            if (!visible) return;
            if (editing) {
              form.setFieldsValue(formValuesFromRecord(editing));
              return;
            }
            form.setFieldsValue(emptyIngredient);
          }}
          extra={
            <Space>
              <Button onClick={closeDrawer} disabled={isSaving}>Cancel</Button>
              <Button action={editing ? 'edit' : 'add'} type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
                {editing ? 'Update Ingredient' : 'Create Ingredient'}
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => saveIngredient(values)}
            initialValues={emptyIngredient}
            autoComplete="off"
          >
            <Form.Item
              label="Ingredient Name"
              name="name"
              rules={[
                { required: true, whitespace: true, message: 'Please enter the ingredient name' },
                { max: 255, message: 'Name must be 255 characters or fewer' },
              ]}
            >
              <Input placeholder="e.g., All-Purpose Flour" maxLength={255} />
            </Form.Item>
            <Form.Item
              label="Referred Food"
              name="referred_food"
              rules={[{ max: 255, message: 'Referred food must be 255 characters or fewer' }]}
            >
              <Input placeholder="Food this ingredient refers to" maxLength={255} allowClear />
            </Form.Item>
            <Form.Item
              label="Category"
              name="category"
              rules={[
                { required: true, whitespace: true, message: 'Please select or enter a category' },
                { max: 100, message: 'Category must be 100 characters or fewer' },
              ]}
            >
              <AutoComplete
                options={categorySuggestions}
                placeholder="Select or type category (e.g., Baking, Spice)"
                filterOption={(inputValue, option) =>
                  (option?.value ?? '').toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Form.Item>

            <Divider orientation="left" plain>Nutrition / 100 gr</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Calories / 100 gr" name="calories">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="kcal / 100 gr" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Protein (g) / 100 gr" name="protein_gr">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Fat (g) / 100 gr" name="fat_gr">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Carbohydrate (g) / 100 gr" name="carbo_gr">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Standard Reference"
              name="standard_ref"
              rules={[{ max: 100, message: 'Standard reference must be 100 characters or fewer' }]}
            >
              <Input placeholder="e.g., USDA FDC, TS 1234" maxLength={100} allowClear />
            </Form.Item>
            <Form.Item
              label="Database Source"
              name="database_source"
              rules={[{ max: 100, message: 'Database source must be 100 characters or fewer' }]}
            >
              <AutoComplete
                options={sourceSuggestions}
                placeholder="Select or type source"
                filterOption={(inputValue, option) =>
                  (option?.value ?? '').toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Form.Item>
          </Form>
        </Drawer>
      </div>
    </>
  );
};

export default CorFoodIngredientsList;
